import { formatLean } from './choropleth'
import { NATIONAL_TWO_PARTY_DEM_SHARE, type LeanCyclePoint } from '../../types/geoAnalytics'

export interface LeanBreakdownRow {
  year: number
  /** District two-party Dem share (percent), or null when the national baseline is unknown. */
  districtDemSharePct: number | null
  /** National two-party Dem share (percent), or null when unknown for this cycle. */
  nationalDemSharePct: number | null
  cycleLean: number
  cycleLeanFmt: string
}

export interface LeanReconciliation {
  year: number
  rawMarginFmt: string
  nationalMarginFmt: string
  leanFmt: string
}

export interface LeanBreakdown {
  rows: LeanBreakdownRow[]
  overallLean: number | null
  overallLeanFmt: string | null
  reconciliation: LeanReconciliation | null
}

/**
 * Pure derivation for the lean explainer. Lean is DEFINED as districtShare − national,
 * so we invert that identity to recover the district two-party Dem share and the raw
 * presidential margin per cycle — no election-results payload required. Reconciliation
 * is tied to the most recent cycle with a known national baseline.
 */
export function buildLeanBreakdown(leanByCycle: LeanCyclePoint[]): LeanBreakdown {
  const rows: LeanBreakdownRow[] = [...leanByCycle]
    .sort((a, b) => a.year - b.year)
    .map((c) => {
      const national = NATIONAL_TWO_PARTY_DEM_SHARE[c.year] ?? null
      return {
        year: c.year,
        districtDemSharePct: national == null ? null : c.lean + national,
        nationalDemSharePct: national,
        cycleLean: c.lean,
        cycleLeanFmt: formatLean(c.lean),
      }
    })

  // overallLean/overallLeanFmt validate the averaging invariant (asserted in the tests).
  // The UI intentionally renders the authoritative `leanFormatted` badge value instead,
  // so the component does not consume these — they document the pure-function contract.
  const overallLean =
    leanByCycle.length === 0
      ? null
      : leanByCycle.reduce((sum, c) => sum + c.lean, 0) / leanByCycle.length

  const recCycle = [...leanByCycle]
    .filter((c) => NATIONAL_TWO_PARTY_DEM_SHARE[c.year] != null)
    .sort((a, b) => b.year - a.year)[0]

  let reconciliation: LeanReconciliation | null = null
  if (recCycle) {
    const national = NATIONAL_TWO_PARTY_DEM_SHARE[recCycle.year]
    const districtShare = recCycle.lean + national
    reconciliation = {
      year: recCycle.year,
      rawMarginFmt: formatLean(2 * districtShare - 100),
      nationalMarginFmt: formatLean(2 * national - 100),
      leanFmt: formatLean(recCycle.lean),
    }
  }

  return {
    rows,
    overallLean,
    overallLeanFmt: overallLean == null ? null : formatLean(overallLean),
    reconciliation,
  }
}
